import type { GameView, SeatView } from "@numbet/engine";
import { Avatar } from "../components/ui.tsx";
import { formatChips, formatValue, secondsLeft } from "../format.ts";
import { t } from "../i18n.ts";

interface SeatsProps {
  view: GameView;
  myId: string;
  connected: Set<string>;
  now: number;
}

export function Seats({ view, myId, connected, now }: SeatsProps) {
  const hand = view.hand;
  const winners = new Set(hand?.result?.pots.flatMap((p) => p.winners) ?? []);
  return (
    <section className="seats" aria-label={t.players(view.players.length, 8)}>
      {view.players.map((seat) => (
        <Seat key={seat.id} seat={seat} view={view} isMe={seat.id === myId} online={connected.has(seat.id)} winner={winners.has(seat.id)} now={now} />
      ))}
    </section>
  );
}

export function clock(deadline: number | null, now: number): string {
  if (deadline === null) return "";
  const s = secondsLeft(deadline, now);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

function statusText(seat: SeatView, online: boolean, view: GameView, now: number, winner: boolean): string {
  if (seat.eliminated) return t.eliminated;
  if (view.hand?.phase === "complete" && seat.inHand) return winner ? `★ ${t.wins}` : seat.status === "folded" ? `× ${t.folded}` : "";
  if (!online) return t.offline;
  if (seat.sittingOut) return t.sittingOut;
  if (seat.status === "folded") return `× ${t.folded}`;
  if (seat.status === "allIn") return t.allInStatus;
  if (view.hand?.phase === "estimate") return seat.hasEstimate ? `✓ ${t.estimated}` : "…";
  if (seat.isToAct) return t.statusTurn(clock(view.hand?.deadline ?? null, now));
  return t.statusWaiting;
}

function Seat({ seat, view, isMe, online, winner, now }: { seat: SeatView; view: GameView; isMe: boolean; online: boolean; winner: boolean; now: number }) {
  const hand = view.hand;
  const deadline = hand?.deadline ?? null;
  const progress = seat.isToAct && deadline !== null ? (deadline - now) / view.actionTimeMs : null;
  const out = seat.status === "folded" || seat.eliminated;
  const position = seat.isButton ? "D" : seat.isSmallBlind ? "SB" : seat.isBigBlind ? "BB" : "";
  const label = `${seat.name}${isMe ? ` (${t.you})` : ""}${position ? ` · ${position}` : ""}`;
  const classes = [
    "seat",
    seat.isToAct && "to-act",
    isMe && "me",
    seat.status === "folded" && "folded",
    (seat.eliminated || (!seat.inHand && view.phase === "playing")) && "out",
    winner && hand?.phase === "complete" && "winner",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <article className={classes} aria-current={seat.isToAct ? "true" : undefined}>
      <Avatar id={seat.id} name={seat.name} avatar={seat.avatar} progress={progress} out={out} />
      <span className="seat-name">{label}</span>
      <span className="seat-meta">
        <span className="num">{formatChips(seat.chips)}</span>
        {seat.roundBet > 0 && hand?.phase !== "complete" && (
          <span key={seat.roundBet} className="seat-bet bump">
            {" "}
            · {t.stake(formatChips(seat.roundBet))}
          </span>
        )}
      </span>
      <span className="seat-status">{statusText(seat, online, view, now, winner)}</span>
      {seat.estimate !== null && hand && (
        <span className="seat-estimate num">
          {formatValue(seat.estimate, hand.question.unit, hand.question.decimals)}
          {seat.deviation !== null && (seat.deviation === 0 ? " 🎯" : ` ±${formatValue(seat.deviation, "", hand.question.decimals)}`)}
        </span>
      )}
    </article>
  );
}
